import { PushNotification } from '@sit/graphql';
import isEqual from 'lodash/isEqual';
import { useCallback, useContext, useEffect, useState } from 'react';
import { NotificationHandlerContext } from '../NotificationHandlerContext';
import { NotificationHandler, NotificationTopic } from '../NotificationHandlerService';

const handleNotificationMatch = (
  notification: PushNotification,
  topic: NotificationTopic,
  params: Record<string | number, unknown> | null = null,
): PushNotification | null => {
  const topicMatch = notification.topic === topic;

  const { data } = notification;
  const paramsMatch = !params ? true : data && Object.keys(params).every((key) => isEqual(data[key], params[key]));

  return topicMatch && paramsMatch ? notification : null;
};

export const useOnLatestNotification = (topic: NotificationTopic, handler: NotificationHandler) => {
  const { service: notificationHandlerService } = useContext(NotificationHandlerContext);

  return useEffect(() => notificationHandlerService.addHandler(topic, handler), [topic, handler, notificationHandlerService]);
};

export function useLatestNotification(
  topic: NotificationTopic,
  params?: Record<string | number, unknown>,
  subscribeToNotification?: NotificationHandler,
): PushNotification | undefined {
  const { service: notificationHandlerService } = useContext(NotificationHandlerContext);
  const [notification, setNotification] = useState<PushNotification | undefined>(undefined);

  const handleNotification = useCallback(
    (notification: PushNotification): void => {
      if (handleNotificationMatch(notification, topic, params)) {
        subscribeToNotification?.(notification);
        setNotification(notification);
      }
    },
    [params, topic, subscribeToNotification],
  );

  useEffect(
    () => notificationHandlerService.addHandler(topic, handleNotification),
    [topic, handleNotification, notificationHandlerService],
  );

  return notification;
}
